import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import Marquee from 'react-fast-marquee'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import {
  useDesktopMediaQuery,
  useTabletDownMediaQuery
} from 'utils/hooks/useMediaQueries'
import { browserHelpers, dateHelpers, stringHelpers } from 'utils'
import PlayerActions from 'reducers/Player'
import { thunks as DownloadThunks } from 'reducers/Downloads'
import {
  PlaybarWrapper,
  CoverArt,
  ReleaseWrapper,
  ReleaseArtist,
  ReleaseTitle,
  ReleaseDetailsWrapper,
  OptionsWrapper,
  PlayerDetailsWrapper,
  PlayOptionsWrapper,
  DurationWrapper,
  SliderWrapper,
  ProgressRow,
  TimelinePreviewLabel,
  TimelinePreviewMarker,
  IconButtonFrame,
  VolumeControlWrapper,
  VolumePopover,
  VolumeRange,
  VolumeRangeFrame,
  ScreenReaderText
} from './NewPlaybar.styles'
import { IconButton, Tonality } from '@dsny/dsny-component-library'
import MobilePlayerSheet from './MobilePlayerSheet'
import { albumCovertArtPlaceholder } from 'images'
import {
  Download,
  Forward10,
  Info,
  PauseFilled,
  PlayArrowFilled,
  Replay10,
  SkipNextFilled,
  SkipPreviousFilled,
  Volumeoff,
  Volumeup
} from '@dsny/dsny-component-library/icons'
import Slider from 'rc-slider'
import TrackPlaylistDropdown from '../Dropdowns/PlaylistDropdown/TrackPlaylistDropdown'
import usePlayerControls from './usePlayerControls'

const DEFAULT_DOWNLOAD_FORMAT = 'mp3'
const PLAYBAR_DOWNLOAD_SOURCE = 'player'
const PLAYBAR_CONTENT_GAP = 16
const PLAYBAR_NOTIFICATION_GAP = 24
const NOTIFICATION_ITEM_BOTTOM_MARGIN = 15
const DEFAULT_NOTIFICATION_BOTTOM_OFFSET = 20
const VOLUME_CONTROL_CLOSE_DELAY = 180

const NewPlaybar = () => {
  const playbarRef = useRef(null)
  const timelineRef = useRef(null)
  const volumeCloseTimeoutRef = useRef(null)
  const isVolumePointerInsideRef = useRef(false)
  const isVolumeInteractingRef = useRef(false)
  const removeVolumePointerEndListenerRef = useRef(null)
  const theme = useTheme()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const responsiveBreakpointsV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2
  const isDesktop = useDesktopMediaQuery()
  const isCompactPlaybar = useTabletDownMediaQuery()
  const {
    isPlaying,
    currentTime,
    displayDuration,
    selectedSong,
    localVolume,
    isMuted,
    isSliderDragging,
    isFirstSong,
    isLastSong,
    handleTogglePlay,
    handleSkipBack,
    handleSkipFwd,
    handleReplayTen,
    handleForwardTen,
    handleVolumeChange,
    handleVolumeIconClick,
    hasNoSong,
    setIsSliderDragging
  } = usePlayerControls()

  const [isMobilePlayerSheetOpen, setIsMobilePlayerSheetOpen] = useState(false)
  const [sliderCurrentTime, setSliderCurrentTime] = useState(currentTime)
  const [isHovered, setIsHovered] = useState(false)
  const [timelinePreview, setTimelinePreview] = useState(null)
  const [isVolumeControlOpen, setIsVolumeControlOpen] = useState(false)
  const { coverart, title, artist } = selectedSong
  const sliderValue = hasNoSong
    ? 0
    : isSliderDragging
      ? sliderCurrentTime
      : currentTime
  const sliderMax = hasNoSong ? 0 : displayDuration
  const volumeValue = isMuted ? 0 : localVolume
  const VolumeIcon = isMuted || volumeValue === 0 ? Volumeoff : Volumeup
  const isDownloadDisabled =
    hasNoSong ||
    selectedSong?.candownload === false ||
    !browserHelpers.browserAllowsDownload
  const isPlaylistDisabled = hasNoSong || !selectedSong?.trackid
  const selectedReleaseId = selectedSong?.releaseid || selectedSong?.release_id
  const canOpenRelease = !hasNoSong && Boolean(selectedReleaseId)

  useEffect(() => {
    if (!isSliderDragging) {
      setSliderCurrentTime(currentTime)
    }
  }, [currentTime, isSliderDragging])

  useEffect(
    () => () => {
      window.clearTimeout(volumeCloseTimeoutRef.current)
      removeVolumePointerEndListenerRef.current?.()
    },
    []
  )

  useEffect(() => {
    const root = document.documentElement

    // Activates the scoped notification offset rule in styles.css. Legacy
    // playbar users never get the attribute, so their toasts keep the
    // react-notifications-component default position.
    root.setAttribute('data-new-playbar', 'true')

    const updatePlaybarOffsets = () => {
      if (!playbarRef.current) return

      const { top } = playbarRef.current.getBoundingClientRect()
      const playbarOffset = window.innerHeight - top
      const offset = Math.max(
        playbarOffset +
          PLAYBAR_NOTIFICATION_GAP -
          NOTIFICATION_ITEM_BOTTOM_MARGIN,
        DEFAULT_NOTIFICATION_BOTTOM_OFFSET
      )

      root.style.setProperty(
        '--playbar-notification-bottom-offset',
        `${Math.ceil(offset)}px`
      )
      root.style.setProperty(
        '--playbar-content-bottom-offset',
        `${Math.ceil(playbarOffset + PLAYBAR_CONTENT_GAP)}px`
      )
    }

    updatePlaybarOffsets()
    const animationFrameId = window.requestAnimationFrame(
      updatePlaybarOffsets
    )

    let resizeObserver
    if (window.ResizeObserver && playbarRef.current) {
      resizeObserver = new window.ResizeObserver(updatePlaybarOffsets)
      resizeObserver.observe(playbarRef.current)
    }

    window.addEventListener('resize', updatePlaybarOffsets)
    window.addEventListener('orientationchange', updatePlaybarOffsets)

    return () => {
      window.cancelAnimationFrame(animationFrameId)
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updatePlaybarOffsets)
      window.removeEventListener('orientationchange', updatePlaybarOffsets)
      root.style.removeProperty('--playbar-content-bottom-offset')
      root.style.removeProperty('--playbar-notification-bottom-offset')
      root.removeAttribute('data-new-playbar')
    }
  }, [])

  useEffect(() => {
    if (!isVolumeControlOpen) return undefined

    const closeVolumeControl = (event) => {
      if (!playbarRef.current?.contains(event.target)) {
        setIsVolumeControlOpen(false)
      }
    }

    const closeVolumeControlOnEscape = (event) => {
      if (event.key === 'Escape') {
        setIsVolumeControlOpen(false)
      }
    }

    document.addEventListener('pointerdown', closeVolumeControl)
    document.addEventListener('keydown', closeVolumeControlOnEscape)

    return () => {
      document.removeEventListener('pointerdown', closeVolumeControl)
      document.removeEventListener('keydown', closeVolumeControlOnEscape)
    }
  }, [isVolumeControlOpen])

  const onInfoReleaseClick = (e) => {
    if (!canOpenRelease) return
    e.stopPropagation()
    navigate(`/releases/releaseInfo/${selectedReleaseId}`)
  }

  // On the compact playbar, only the cover art navigates to the release;
  // taps anywhere else must bubble up to the wrapper and expand the sheet.
  const handleReleaseDetailsClick = (e) => {
    if (isCompactPlaybar) return
    onInfoReleaseClick(e)
  }

  const handleCoverArtClick = (e) => {
    if (!isCompactPlaybar) return
    onInfoReleaseClick(e)
  }

  const togglePlaybar = () => {
    setIsMobilePlayerSheetOpen(!isMobilePlayerSheetOpen)
  }

  const handleDisabledButtonClick = (e) => {
    e.stopPropagation()
    e.preventDefault()
  }

  const handleSliderRelease = (value) => {
    if (hasNoSong) return
    dispatch(PlayerActions.seekTimeRequest(value))
    setSliderCurrentTime(value)
    setIsSliderDragging(false)
  }

  const handleSliderChange = (value) => {
    if (hasNoSong) return
    setSliderCurrentTime(value)
    setIsSliderDragging(true)
  }

  const getTimelinePreviewFromEvent = (event) => {
    const timelineBounds = timelineRef.current?.getBoundingClientRect()

    if (!timelineBounds || hasNoSong || displayDuration === 0) {
      return null
    }

    const offsetX = Math.min(
      Math.max(event.clientX - timelineBounds.left, 0),
      timelineBounds.width
    )
    const percentage = (offsetX / timelineBounds.width) * 100
    const time = (offsetX / timelineBounds.width) * displayDuration

    return { percentage, time }
  }

  const handleTimelinePointerMove = (event) => {
    const preview = getTimelinePreviewFromEvent(event)

    if (preview) {
      setTimelinePreview(preview)
    }
  }

  const handleTimelinePointerEnter = (event) => {
    setIsHovered(true)
    handleTimelinePointerMove(event)
  }

  const handleTimelinePointerLeave = () => {
    setIsHovered(false)
    setTimelinePreview(null)
  }

  const handleDownloadTrack = (e) => {
    e.stopPropagation()
    if (isDownloadDisabled) return

    if (selectedSong.assettype === 'video') {
      dispatch(
        DownloadThunks.addVideoToDownloadQueue(
          selectedSong,
          PLAYBAR_DOWNLOAD_SOURCE
        )
      )
      return
    }

    dispatch(
      DownloadThunks.addSongToDownloadQueue(selectedSong, {
        format: DEFAULT_DOWNLOAD_FORMAT,
        source: PLAYBAR_DOWNLOAD_SOURCE
      })
    )
  }

  const handleVolumeButtonClick = (e) => {
    e.stopPropagation()
    setIsVolumeControlOpen(true)
    handleVolumeIconClick()
  }

  const clearVolumeCloseIntent = () => {
    window.clearTimeout(volumeCloseTimeoutRef.current)
  }

  const openVolumeControl = () => {
    clearVolumeCloseIntent()
    setIsVolumeControlOpen(true)
  }

  const closeVolumeControl = ({ immediate = false } = {}) => {
    clearVolumeCloseIntent()

    if (!immediate && isVolumeInteractingRef.current) return

    if (!immediate) {
      volumeCloseTimeoutRef.current = window.setTimeout(() => {
        setIsVolumeControlOpen(false)
      }, VOLUME_CONTROL_CLOSE_DELAY)
      return
    }

    setIsVolumeControlOpen(false)
  }

  const handleVolumeControlPointerEnter = () => {
    isVolumePointerInsideRef.current = true
    openVolumeControl()
  }

  const handleVolumeControlPointerLeave = () => {
    isVolumePointerInsideRef.current = false
    closeVolumeControl()
  }

  const handleVolumeControlBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      closeVolumeControl({ immediate: true })
    }
  }

  const handleVolumeRangePointerDown = () => {
    clearVolumeCloseIntent()
    isVolumeInteractingRef.current = true
    removeVolumePointerEndListenerRef.current?.()

    const handlePointerEnd = () => {
      const removePointerEndListener = removeVolumePointerEndListenerRef.current
      removeVolumePointerEndListenerRef.current = null
      removePointerEndListener?.()
      isVolumeInteractingRef.current = false

      if (!isVolumePointerInsideRef.current) {
        closeVolumeControl()
      }
    }

    window.addEventListener('pointerup', handlePointerEnd)
    window.addEventListener('pointercancel', handlePointerEnd)
    removeVolumePointerEndListenerRef.current = () => {
      window.removeEventListener('pointerup', handlePointerEnd)
      window.removeEventListener('pointercancel', handlePointerEnd)
    }
  }

  const renderTitle = (title) => {
    const maxLength = isDesktop ? 90 : isCompactPlaybar ? 35 : 45
    const marqueeWidth = isCompactPlaybar ? '90%' : '45%'

    return title?.length > maxLength ? (
      <Marquee style={{ width: marqueeWidth }} pauseOnHover speed={20}>
        <ReleaseTitle $responsiveBreakpointsV2={responsiveBreakpointsV2}>
          {title}
        </ReleaseTitle>
      </Marquee>
    ) : (
      <ReleaseTitle $responsiveBreakpointsV2={responsiveBreakpointsV2}>
        {title}
      </ReleaseTitle>
    )
  }

  const renderArtist = (artist) => {
    const maxLength = isDesktop ? 90 : isCompactPlaybar ? 35 : 45
    const marqueeWidth = isCompactPlaybar ? '90%' : '45%'

    return artist?.length > maxLength ? (
      <Marquee style={{ width: marqueeWidth }} pauseOnHover speed={20}>
        <ReleaseArtist $responsiveBreakpointsV2={responsiveBreakpointsV2}>
          {artist}
        </ReleaseArtist>
      </Marquee>
    ) : (
      <ReleaseArtist $responsiveBreakpointsV2={responsiveBreakpointsV2}>
        {artist}
      </ReleaseArtist>
    )
  }

  const renderTooltip = ({ children, content, placement = 'top' }) => {
    if (!content) return children

    return (
      <Tonality.Tooltip
        mode="light"
        content={content}
        placement={placement}
        containerStyles={{ padding: '4px 8px' }}
      >
        {children}
      </Tonality.Tooltip>
    )
  }

  const renderIconButton = ({
    label,
    Icon,
    onClick,
    tooltip,
    tooltipPlacement = 'top',
    disabled = false,
    primary = false,
    active = false,
    iconSize = 21,
    size = 38
  }) =>
    renderTooltip({
      content: disabled ? undefined : tooltip,
      placement: tooltipPlacement,
      children: (
        <IconButtonFrame
          $active={active}
          $disabled={disabled}
          $primary={primary}
          $size={size}
        >
          <IconButton onClick={disabled ? handleDisabledButtonClick : onClick}>
            <Icon
              aria-hidden="true"
              width={iconSize}
              height={iconSize}
              color="currentColor"
            />
            <ScreenReaderText>{label}</ScreenReaderText>
          </IconButton>
        </IconButtonFrame>
      )
    })

  const renderPlaylistButton = () =>
    renderTooltip({
      content: isPlaylistDisabled
        ? undefined
        : t('playlist.add-to-playlist-tooltip'),
      children: (
        <IconButtonFrame $disabled={isPlaylistDisabled} $size={38}>
          <TrackPlaylistDropdown
            track={selectedSong}
            disabled={isPlaylistDisabled}
            iconColor="currentColor"
            triggerClassName="playbar-playlist-trigger"
            triggerLabel={t('playlist.add-to-playlist')}
          />
        </IconButtonFrame>
      )
    })

  const currentTimeText = hasNoSong
    ? '-:--'
    : stringHelpers.formatTime(currentTime)
  const durationText = hasNoSong
    ? '-:--'
    : dateHelpers.getStringFromSeconds(displayDuration)

  return (
    <>
      <PlaybarWrapper
        ref={playbarRef}
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
        data-playbar-root="true"
        onClick={isCompactPlaybar ? togglePlaybar : undefined}
      >
        <ReleaseDetailsWrapper
          $canOpenRelease={canOpenRelease}
          $responsiveBreakpointsV2={responsiveBreakpointsV2}
          aria-disabled={!canOpenRelease}
          aria-label={
            canOpenRelease
              ? `${t('general.view-release')}: ${title || artist}`
              : undefined
          }
          data-can-open-release={canOpenRelease ? 'true' : undefined}
          tabIndex={canOpenRelease ? 0 : -1}
          type="button"
          onClick={handleReleaseDetailsClick}
        >
          <CoverArt
            $isCompact={isCompactPlaybar}
            $responsiveBreakpointsV2={responsiveBreakpointsV2}
            src={coverart || albumCovertArtPlaceholder}
            alt="Cover art"
            onClick={handleCoverArtClick}
          />
          <ReleaseWrapper>
            {renderTitle(hasNoSong ? '' : title)}
            {renderArtist(hasNoSong ? '' : artist)}
          </ReleaseWrapper>
        </ReleaseDetailsWrapper>

        {isCompactPlaybar ? (
          <PlayOptionsWrapper>
            {renderIconButton({
              label: isPlaying ? 'Pause' : t('general.play'),
              Icon: isPlaying && !hasNoSong ? PauseFilled : PlayArrowFilled,
              onClick: handleTogglePlay,
              disabled: hasNoSong,
              primary: true,
              iconSize: 24,
              size: 40
            })}
          </PlayOptionsWrapper>
        ) : (
          <>
            <PlayerDetailsWrapper>
              <PlayOptionsWrapper $isDesktop={isDesktop}>
                {renderIconButton({
                  label: 'Previous track',
                  Icon: SkipPreviousFilled,
                  onClick: handleSkipBack,
                  disabled: hasNoSong || isFirstSong
                })}
                {renderIconButton({
                  label: 'Back 10 seconds',
                  Icon: Replay10,
                  onClick: handleReplayTen,
                  disabled: hasNoSong
                })}
                {renderIconButton({
                  label: isPlaying ? 'Pause' : t('general.play'),
                  Icon: isPlaying && !hasNoSong ? PauseFilled : PlayArrowFilled,
                  onClick: handleTogglePlay,
                  disabled: hasNoSong,
                  primary: true,
                  iconSize: 24,
                  size: 40
                })}
                {renderIconButton({
                  label: 'Forward 10 seconds',
                  Icon: Forward10,
                  onClick: handleForwardTen,
                  disabled: hasNoSong
                })}
                {renderIconButton({
                  label: 'Next track',
                  Icon: SkipNextFilled,
                  onClick: handleSkipFwd,
                  disabled: hasNoSong || isLastSong
                })}
              </PlayOptionsWrapper>

              <ProgressRow>
                <DurationWrapper $hasNoSong={hasNoSong}>
                  {currentTimeText}
                </DurationWrapper>
                <SliderWrapper
                  ref={timelineRef}
                  onPointerEnter={handleTimelinePointerEnter}
                  onPointerMove={handleTimelinePointerMove}
                  onPointerLeave={handleTimelinePointerLeave}
                >
                  <Slider
                    value={sliderValue}
                    defaultValue={0}
                    min={0}
                    max={sliderMax}
                    step={0.01}
                    className="cursor-pointer"
                    trackStyle={{
                      backgroundColor:
                        isHovered && !hasNoSong
                          ? theme.colors.player50
                          : theme.colors.neutralW100
                    }}
                    railStyle={{
                      backgroundColor: theme.colors.neutralW30
                    }}
                    handleStyle={{
                      borderColor:
                        isHovered && !hasNoSong
                          ? theme.colors.neutralW100
                          : 'transparent',
                      width: isHovered && !hasNoSong ? 12 : 0,
                      height: isHovered && !hasNoSong ? 12 : 0,
                      backgroundColor:
                        isHovered && !hasNoSong
                          ? theme.colors.neutralW100
                          : 'transparent'
                    }}
                    onChange={handleSliderChange}
                    onAfterChange={handleSliderRelease}
                  />
                  {timelinePreview && !hasNoSong && (
                    <>
                      <TimelinePreviewLabel
                        $percentage={timelinePreview.percentage}
                      >
                        {dateHelpers.getStringFromSeconds(timelinePreview.time)}
                      </TimelinePreviewLabel>
                      <TimelinePreviewMarker
                        $percentage={timelinePreview.percentage}
                      />
                    </>
                  )}
                </SliderWrapper>
                <DurationWrapper $hasNoSong={hasNoSong}>
                  {durationText}
                </DurationWrapper>
              </ProgressRow>
            </PlayerDetailsWrapper>

            <OptionsWrapper $isDesktop={isDesktop}>
              {renderIconButton({
                label: t('general.download-this-song'),
                Icon: Download,
                onClick: handleDownloadTrack,
                disabled: isDownloadDisabled,
                tooltip: t('general.download-this-song')
              })}
              {renderPlaylistButton()}
              {renderIconButton({
                label: t('general.view-release'),
                Icon: Info,
                onClick: onInfoReleaseClick,
                disabled: !canOpenRelease,
                tooltip: t('general.view-release')
              })}
              <VolumeControlWrapper
                onPointerEnter={handleVolumeControlPointerEnter}
                onPointerLeave={handleVolumeControlPointerLeave}
                onFocus={openVolumeControl}
                onBlur={handleVolumeControlBlur}
              >
                {renderIconButton({
                  label: isMuted ? t('general.unmute') : t('general.mute'),
                  Icon: VolumeIcon,
                  onClick: handleVolumeButtonClick,
                  active: isVolumeControlOpen,
                  tooltip: isMuted ? t('general.unmute') : t('general.mute'),
                  tooltipPlacement: 'bottom'
                })}
                <VolumePopover $isOpen={isVolumeControlOpen}>
                  <VolumeRangeFrame>
                    <VolumeRange
                      $isMuted={isMuted}
                      type="range"
                      value={volumeValue}
                      min={0}
                      max={100}
                      step="any"
                      aria-label="Volume Slider"
                      style={{ backgroundSize: `${volumeValue}% 100%` }}
                      onPointerDown={handleVolumeRangePointerDown}
                      onChange={handleVolumeChange}
                    />
                  </VolumeRangeFrame>
                </VolumePopover>
              </VolumeControlWrapper>
            </OptionsWrapper>
          </>
        )}
      </PlaybarWrapper>
      {isMobilePlayerSheetOpen && (
        <MobilePlayerSheet toggleExpanded={togglePlaybar} />
      )}
    </>
  )
}

export default NewPlaybar
